/**
 * DriversPulse — the DAAily Drivers block inside Home's "This month" card
 * (Sep 2026, compacted the same month so Home reads calmer).
 *
 *   - "You haven't nominated anyone in September yet" (or a thank-you once
 *     the caller has), with a Nominate button.
 *   - Time left in the month (Europe/Zurich) as one line, ticking every
 *     30 seconds; turns brand-colored and counts hours in the last 48 hours.
 *   - Trending values so far: ranked value names with their medal, no counts
 *     (the server withholds the list until the month has a few nominations).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass } from 'lucide-react';
import { monthEndInstant, type DriversPulse as Pulse } from '@daaily/shared';
import { Icon } from '../design/Icon';
import { btnPrimary, btnSecondary, overline } from '../design/ui';
import { Medal } from '../drivers/Trophy';
import { monthLabel } from '../drivers/lib/format';

function useNow(stepMs: number) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), stepMs);
    return () => window.clearInterval(id);
  }, [stepMs]);
  return now;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function TimeLeft({ now }: { now: Date }) {
  const ms = Math.max(0, monthEndInstant(now).getTime() - now.getTime());
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  const urgent = ms < 48 * 3_600_000;
  const text = days >= 2
    ? `${plural(days, 'day')} left to nominate`
    : ms >= 3_600_000
      ? `${plural(Math.floor(ms / 3_600_000), 'hour')} left to nominate`
      : `${plural(mins, 'minute')} left to nominate`;
  return (
    <p
      className={`inline-flex items-center gap-1.5 text-xs ${urgent ? 'text-brand font-medium' : 'text-fg-muted'}`}
      role="timer"
      aria-label={`${days} days, ${hours} hours and ${mins} minutes left to nominate this month`}
    >
      <Icon as={Hourglass} size={14} /> {text}
    </p>
  );
}

export function DriversPulse({ pulse }: { pulse: Pulse }) {
  const now = useNow(30_000);
  const month = monthLabel(pulse.month).split(' ')[0];

  if (!pulse.open) {
    return (
      <section aria-labelledby="drivers-pulse-h">
        <p className={overline}>DAAily Drivers</p>
        <h3 id="drivers-pulse-h" className="mt-1 text-sm font-medium text-fg">{month} is closed</h3>
        <p className="mt-0.5 text-sm text-fg-muted">Winners are out. Nominations open again on the 1st.</p>
      </section>
    );
  }

  return (
    <section className="space-y-2.5" aria-labelledby="drivers-pulse-h">
      <div>
        <p className={overline}>DAAily Drivers · {month}</p>
        <h3 id="drivers-pulse-h" className="mt-1 text-sm font-medium text-fg">
          {pulse.hasNominated
            ? 'Thanks for recognizing a colleague'
            : `You haven’t nominated anyone in ${month} yet`}
        </h3>
      </div>
      <TimeLeft now={now} />
      {pulse.trending.length > 0 && (
        <ol className="flex flex-wrap gap-x-3 gap-y-1" aria-label="Trending values this month">
          {pulse.trending.map((t, i) => (
            <li key={t.valueId} className={`inline-flex items-center gap-1 text-xs ${i === 0 ? 'text-fg font-medium' : 'text-fg-muted'}`}>
              <Medal valueId={t.valueId} size={18} />
              <span className="sr-only">{i + 1}. </span>{t.valueName}
            </li>
          ))}
        </ol>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-0.5">
        <Link to="/drivers/new" className={pulse.hasNominated ? btnSecondary : btnPrimary}>
          {pulse.hasNominated ? 'Nominate someone else' : 'Nominate a colleague'}
        </Link>
        <Link to="/drivers" className="text-sm text-fg-muted hover:text-fg underline-offset-2 hover:underline">Trophy cabinet</Link>
      </div>
    </section>
  );
}
